<template>
    <div class="container">
        <!-- BEGIN row -->
        <div v-if="session">
            <!-- Garmin activity view -->
            <div v-if="session.source === 'garmin'">
                <div class="row justify-content-around mb-4">
                    <div class="col-8">
                        <h2> Session Name: {{ session.name }}, {{ session.timestamp?.toDate ? session.timestamp.toDate().toDateString() : new Date(session.timestamp).toDateString() }} </h2>
                    </div>
                    <div class="col-4 d-flex justify-content-end gap-2">
                        <button @click="$router.push(`/sessions/${session.id}/post-ride-processing`)" class="btn btn-warning text-white fw-bold">
                            <i class="mdi mdi-memory me-1"></i> Post-Ride Processing
                        </button>
                        <button v-if="isOwner" @click="$router.push(`/sessions/${session.id}/share`)" class="btn btn-secondary">
                            <i class="mdi mdi-share-variant me-1"></i> Share
                        </button>
                        <button @click="$router.back()" class="btn btn-primary">
                            Back
                        </button>
                    </div>
                </div>
                <GarminActivityDetail :session="session" />
            </div>

            <!-- Manually-logged activity (no FIT file, no CdA test data) -->
            <div v-else-if="session.source === 'manual'">
                <div class="row justify-content-around mb-4">
                    <div class="col-8">
                        <h2> Session Name: {{ session.name }}, {{ session.timestamp?.toDate ? session.timestamp.toDate().toDateString() : new Date(session.timestamp).toDateString() }} </h2>
                    </div>
                    <div class="col-4 d-flex justify-content-end gap-2">
                        <button @click="$router.back()" class="btn btn-primary">
                            Back
                        </button>
                    </div>
                </div>
                <ManualActivityDetail :session="session" />
            </div>

            <!-- Standard aero session detail view -->
            <div v-else>
                <div class="row justify-content-around">
                    <!-- BEGIN page-header -->

                    <div class="col-8">
                        <h2> Session Name: {{ session.name }}, {{ session.timestamp?.toDate ? session.timestamp.toDate().toDateString() : new Date(session.timestamp).toDateString() }} </h2>
                    </div>
                    <div class="col-4 d-flex justify-content-end gap-2">
                        <button @click="$router.push(`/sessions/${session.id}/post-ride-processing`)" class="btn btn-warning text-white fw-bold">
                            <i class="mdi mdi-memory me-1"></i> Post-Ride Processing
                        </button>
                        <button @click="$router.push(`/sessions/${session.id}/cfd`)" class="btn btn-info">
                            <i class="mdi mdi-fan me-1"></i> Position Explorer
                        </button>
                        <button @click="$router.push(`/sessions/${session.id}/race-predictor`)" class="btn btn-success text-white">
                            <i class="mdi mdi-flag-checkered me-1"></i> Race Predictor
                        </button>
                        <button v-if="isOwner" @click="$router.push(`/sessions/${session.id}/share`)" class="btn btn-secondary">
                            <i class="mdi mdi-share-variant me-1"></i> Share
                        </button>
                        <button @click="$router.back()" class="btn btn-primary">
                            Back
                        </button>
                    </div>
                </div>

            <div class="error-page" v-if="session.groupedTests && session.groupedTests.length < 2">
                <div class="error-page-content">
                    <h2>
                        NOT ENOUGH AERO TESTS TO REPORT
                    </h2>
                    <h3>At least 2 aero tests are required to perform analysis</h3>
                </div>
            </div>
            <div v-else>
                <div class="row g-2 justify-content-start">
                    <div id="calcs" class="mb-5">
                        <card>
                            <card-header class="with-btn">
                                Race Calculations
                                <card-header-btn />
                            </card-header>

                            <card-body>
                                <table class="table-py-1px fs-10px fw-semibold text-white">
                                    <tbody>
                                        <tr v-if="session.collection">
                                            <td class="pe-3 text-white text-opacity-50 fs-14px">Collection:</td>
                                            <td class="pe-3 fs-14px">{{ session.collection }}</td>
                                        </tr>

                                        <!-- Imported activity files (Garmin FIT) carry no rider
                                             mass, so the metric is omitted entirely. -->
                                        <tr v-if="isAeroSession(session)">
                                            <td class="pe-3 text-white text-opacity-50 fs-14px">System Weight:</td>
                                            <td class="pe-3 fs-14px">{{ calculations.totalMass }} {{ calculations.weightUnit }}</td>
                                        </tr>


                                    </tbody>
                                </table>
                                <br>
                                <form v-on:submit.prevent="onSubmitForm()" method="POST" name="calc_form">
                                    <div class="mb-3 row">
                                        <div class="col-sm-10">

                                            <div class="mb-4">
                                                <label class="form-label">Target Distance ({{ calculations.distanceUnit }})</label>
                                                <input v-model="calculations.targetDistance" type="text" class="form-control form-control-lg fs-14px" placeholder="target race distance" />
                                            </div>
                                            <div class="mb-4">
                                                <label class="form-label">Target Power</label>
                                                <input v-model="calculations.targetWatts" type="text" class="form-control form-control-lg fs-14px" placeholder="target race distance" />
                                            </div>
                                            <div class="form-check form-switch">
                                                <input type="checkbox" class="form-check-input" id="customSwitch1" v-model="calculations.isMetric" @change="onChangeMetric">
                                                <label class="form-check-label" for="customSwitch1">Is metric</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="container">
                                        <div class="btn-text-left">
                                            <button type="submit" class="btn btn-primary">Calculate</button>
                                        </div>
                                    </div>
                                </form>
                            </card-body>

                        </card>
                    </div>

                     <!-- Calculations chart -->
                     <card>
                        <card-header class="with-btn">
                            Savings in watts
                        </card-header>
                        <card-body>
                            <div class="col-xl-12">
                                <VueApexCharts :options="calcOptions" :series="seriesCalc"></VueApexCharts>
                            </div>
                        </card-body>
                    </card>


                    <div class="shared-sessions-content">
                        <card>
                            <card-header class="with-btn">
                                Tests
                            </card-header>
                            <card-body>
                                <div class="table-responsive">
                                    <table class="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Tags</th>
                                                <th>CdA</th>
                                                <th>Speed</th>
                                                <th>Power</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="test in session.groupedTests" :key="test.id">
                                                <td>{{ test.name }}</td>
                                                <td>{{ test.testRunNotes.toString() }}</td>
                                                <td>{{ test.cda.toFixed(3) }}</td>
                                                <td>{{ formatSpeed(test.averageSpeed) }}</td>
                                                <td>{{ test.averagePower.toFixed(1) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </card-body>
                        </card>
                    </div>

                    <!-- Aero Test Compare Chart -->
                    <card>
                        <card-header class="with-btn">
                            AERO TESTS COMPARE
                        </card-header>
                        <card-body>
                            <div class="col-xl-12">
                                <VueApexCharts :options="options" :series="series"></VueApexCharts>
                            </div>
                        </card-body>
                    </card>

                   
                    <hr>
                    NON-GROUPED AERO TESTS DETAILS
                    <hr>
                    <!-- BEGIN col-12 -->
                    <div class="col-xl-12">
                        <!-- BEGIN row -->
                        <div class="row g-2" v-if="session.tests">
                            <!-- BEGIN col-2 -->
                            <div class="col-xl-2 col-lg-4 col-6" v-for="test in session.tests">
                                <card class="h-100">
                                    <card-header>{{ test.name }}</card-header>

                                    <card-body>
                                        <table class="table-py-1px fs-10px fw-semibold text-white">
                                            <tbody>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">CDA:</td>
                                                    <td>{{ test.cda.toFixed(3) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Duration:</td>
                                                    <td>{{ formatDuration(test.duration) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Distance:</td>
                                                    <td>{{ formatDistance(test.distance) }} km</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Air Factor:</td>
                                                    <td>{{ test.airFactor.toFixed(3) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Confidence:</td>
                                                    <td>{{ test.cdaConfidenceScore.toFixed(3) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Avg. Power:</td>
                                                    <td>{{ test.averagePower.toFixed(1) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="pe-3 text-white text-opacity-50">Avg. Speed:</td>
                                                    <td>{{ formatSpeed(test.averageSpeed) }} km/h</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </card-body>
                                </card>
                            </div>
                            <!-- END col-2 -->
                        </div>
                        <!-- END row -->
                    </div>
                    <!-- END col-12 -->

                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script setup>

/*
  imports
*/

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFirebaseAppStore } from '@/stores/storeFirebaseApp'
import { useSessionDataStore } from '@/stores/sessionDataStore'
import VueApexCharts from "vue3-apexcharts"
import GarminActivityDetail from '@/components/features/GarminActivityDetail.vue'
import ManualActivityDetail from '@/components/features/ManualActivityDetail.vue'
import { isAeroSession } from '@/utils/sessionParams'

const route = useRoute()
const router = useRouter()

/*
  store
*/

const firebaseAppStore = useFirebaseAppStore()

/*
Session
*/
const session = ref(null)
session.value = firebaseAppStore.getSessionContent(route.params.id)

// Only the owner sees the Share entry point — same case-insensitive
// comparison used elsewhere (userId is the owner's email, currentUser.id is
// already lowercased).
const isOwner = computed(() => {
    const owner = session.value?.userId
    const me = firebaseAppStore.currentUser?.id
    if (!owner || !me) return false
    return String(owner).toLowerCase() === String(me).toLowerCase()
})

const calculations = reactive({
    targetDistance: 90,
    targetWatts: 170,
    isMetric: true,
    totalMass: session.value?.totalMass || 0,
    weightUnit: ' kg',
    distanceUnit: 'km'
})


/*
data store
*/
const seriesData = ref([])
const timeData = ref([])

const dataStore = useSessionDataStore()
if (session.value) {
    dataStore.populate(session.value)
    seriesData.value = dataStore.calculateSavings(session.value, calculations.targetDistance, calculations.targetWatts)
    timeData.value = dataStore.timeSeries

    calculations.targetWatts = dataStore.calculateAveragePower(session.value)
}

const calcOptions = ref({

    chart: {
        type: 'bar',
        stacked: true
    },
    colors: dataStore.colorsCalc,
    dataLabels: {
        enabled: true,
        formatter: function (val, { seriesIndex, dataPointIndex, w }) {
            let timeSaving = timeData.value[dataPointIndex]
            //return formatDuration(Math.abs(timeSaving))
            let secs = formatDuration(Math.abs(timeSaving))
                if (val < 0.1) {
                    return '+' + secs
                } else if (val === 0.1) {
                    return ''
                } else {
                    return '-' + secs
                }
            //return val + ' watts'

        }
    },
    fill: {
        colors: dataStore.colorsCalc,
        type: 'solid',
    },
    stroke: {
        width: 1,
        colors: ["#fff"]
    },
    tooltip: {
        shared: false,
        x: {
            formatter: function (val) {
                return val
            }
        },
        y: {
            formatter: function (val, { seriesIndex, dataPointIndex, w }) {
                //console.log('series index=' + seriesIndex + ' data index=' + dataPointIndex)
                let timeSaving = timeData.value[dataPointIndex]
                let secs = formatDuration(Math.abs(timeSaving))
                //let secs = val + ' watts'
                if (val < 0.1) {
                    return 'slower by ' + secs
                } else if (val === 0.1) {
                    return 'no change'
                } else {
                    return 'faster by ' + secs
                }
            }
        }
    },
    // title: {
    //     text: 'Savings in duration(mm:ss)',

    //     style: {
    //         fontSize: '16px',
    //         fontFamily: 'Helvetica, Arial, sans-serif',
    //         fontWeight: 'bold',
    //         colors: ["white"],
    //     }
    // },
    yaxis: {
        labels: {
            style: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: dataStore.colors,
            }
        }
    },
    xaxis: {
        type: 'category',
        categories: dataStore.dataLabels,
        labels: {
            style: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: dataStore.colors,
            }
        }
    },
})


const seriesCalc = ref([{
    name: session.value?.name || 'Session',
    data: seriesData.value
}])

const series = ref([{
    name: session.value?.name || 'Session',
    data: dataStore.dataValues
}])

const options = ref({
    chart: {
        type: 'bar',
        fontFamily: 'Helvetica, Arial, sans-serif'
    },
    plotOptions: {
        bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
            horizontal: true,
        }
    },
    dataLabels: {
        enabled: true,
        style: {
            fontSize: '14px',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontWeight: 'bold',
            colors: ["white"],
        }
    },
    fill: {
        colors: undefined,
        opacity: 0.9,
        type: 'solid',
        gradient: {
            shade: 'dark',
            type: "horizontal",
            shadeIntensity: 0.5,
            gradientToColors: undefined,
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 50, 100],
            colorStops: []
        }
    },
    yaxis: {
        labels: {
            style: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: dataStore.colors,
            }
        }
    },
    xaxis: {
        type: 'category',
        categories: dataStore.dataLabels,
        labels: {
            style: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: dataStore.colors,
            }
        }
    },
    fill: {
        type: 'gradient',
        gradient: {
            shade: 'light',
            type: "horizontal",
            shadeIntensity: 0.5,
            gradientToColors: undefined, // optional, if not defined - uses the shades of same color in series
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 50, 100],
            colorStops: []
        }
    }

})


const formatDuration = (timestamp) => {
    var sec_num = parseInt(timestamp, 10); // don't forget the second param
    var hours = Math.floor(sec_num / 3600);
    var minutes = Math.floor((sec_num - (hours * 3600)) / 60);
    var seconds = sec_num - (hours * 3600) - (minutes * 60);

    if (hours < 10) { hours = "0" + hours; }
    if (minutes < 10) { minutes = "0" + minutes; }
    if (seconds < 10) { seconds = "0" + seconds; }
    return minutes + ':' + seconds;
}

const formatDistance = (meters) => {
    var kilometers = (meters / 1000).toFixed(2)
    return kilometers
}

const formatSpeed = (speed) => {
    let dSpeed = (speed * 3600) / 1000
    return dSpeed.toFixed(1)
}

onMounted(() => {

})

const onSubmitForm = async () => {
    var localDistance = calculations.targetDistance
    if (!calculations.isMetric){
        localDistance = (calculations.targetDistance * 1.60934).toFixed()
    }
    console.log('target distance=', calculations.targetDistance)
    // dataStore.targetDistance = calculations.targetDistance
    // dataStore.targetWatts = calculations.targetWatts
    let newData = dataStore.calculateSavings(session.value, calculations.targetDistance, calculations.targetWatts)
   
    timeData.value = dataStore.timeSeries

    // timeData.value.forEach(element => {
    //     console.log(' value=' + element)
    // });

    // newData.forEach(element => {
    //     console.log(element)
    // });
    seriesCalc.value = [{
        name: session.value.name,
        data: newData
    }]
}

const onChangeMetric = () => {
    //console.log("is Metric:", calculations.isMetric)
    if (calculations.isMetric) {
        calculations.totalMass = (calculations.totalMass / 2.20462).toFixed()
        calculations.targetDistance = (calculations.targetDistance * 1.60934).toFixed()
        calculations.weightUnit = ' kg'
        calculations.distanceUnit = 'km'
        //console.log("now metric total mass=", calculations.totalMass, " distance=", calculations.targetDistance)
    } else {
        calculations.targetDistance = (calculations.targetDistance * 0.621371).toFixed()
        calculations.totalMass = (calculations.totalMass * 2.20462).toFixed()
        calculations.weightUnit = ' lbs'
        calculations.distanceUnit = 'miles'
        //console.log("now imperial total mass=", calculations.totalMass, " distance=", calculations.targetDistance)
    }

}
</script>

<style scoped>
.btn-text-left {
    text-align: left;
}
</style>
